// A KIADÁS-LAP ÖSSZEGZŐJE — a szűrő-sáv és a lista között: KÉT kártya, nem egy vegyes jelvény-csík.
//
// ══ MIÉRT VÁLTOZOTT (user-döntés) ════════════════════════════════════════════════════════════════════════
// Korábban a devizánkénti pénz-kártyák és az állapot-jelvények EGY flex-sor testvérei voltak, azonos
// térközzel — tehát egyetlen, tagolatlan csíkként olvasódtak, pedig KÉT KÜLÖN kérdésre felelnek:
// „mennyi?" és „mire kell figyelni?". Ráadásul a pénz-kártyák SZÁMA adat-függő (egy deviza = egy kártya),
// így a mögöttük tördelődő jelvény-sor futásonként máshova esett; mobilon kártya és jelvény került egy sorba.
//
// Két kártya: balra a PÉNZ, jobbra az ÁLLAPOT. Mobilon egymás alá csúsznak, tiszta határral.
//
// ══ AMI SZÁNDÉKOSAN NEM EGYFORMA A JOBB KÁRTYÁN ══════════════════════════════════════════════════════════
// Az állapot-sorok között HÁROM különböző fajta van, és ha mind azonos szürke meta-szöveg lenne, a
// cselekvést kérő tételek nem „húznának" jobban, mint egy passzív időpont:
//   • TEENDŐ (besorolásra vár · összeg nélküli sor) → a SZÁM warning-tónusú,
//   • PASSZÍV állapot (utolsó NAV-import) → halvány, elválasztó alatt,
//   • KAPCSOLÓ (elvetett) → gomb, mert szűrőt állít; aktív állapotban jelölt.
//
// A KÉT TEENDŐ (besorolásra vár · összeg nélküli) IS kapcsoló (user-kérés): a szám nem csak jelez, hanem
// egy kattintással le is szűri a listát pontosan azokra a sorokra — a warning-tónus marad, mert attól még
// teendő. A szűrő és a számláló a szerveren UGYANAZON a predikátumon áll, tehát a lista annyi sort ad,
// amennyit a szám mond.
//
// ══ AMI VÁLTOZATLAN, ÉS NEM IS VÁLTOZHAT ═════════════════════════════════════════════════════════════════
// A devizák KÜLÖN állnak, összevont végösszeg NINCS: a bevétel-oldal MNB-árfolyama a BEVÉTELRE van hangolva
// (előző hó vége), és egy második, hallgatólagos átváltás itt olyan számot adna, amit senki nem tud
// visszakövetni. A kártya tehát hasábokra bontja őket, nem összegzi.
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';

import { useNavLastImportDate } from '@/components/expenses/nav-sync-button';
import { SummaryCard, SummaryCards, SummaryMetaRow } from '@/components/shared/summary-cards';
import { Text } from '@/components/ui/text';
import type { ExpenseSummary } from '@atlas/contracts';
import { formatDay } from '@/lib/format-datetime';
import { formatMoney } from '@/lib/format-money';
import { cn } from '@/lib/utils';

/** Az összegző KATTINTHATÓ sorai — mindegyik egy-egy lista-szűrőt kapcsol. */
export type ExpenseSummaryToggle =
  'uncategorized' | 'missingEmployee' | 'missingAmount' | 'dismissed';

export function ExpenseSummaryCards({
  summary,
  locale,
  active,
  onToggle,
}: {
  summary: ExpenseSummary;
  locale: string;
  /** Melyik kapcsoló szűrője áll MOST a listán (aktív állapotban jelölt, újabb kattintás leveszi). */
  active: Readonly<Record<ExpenseSummaryToggle, boolean>>;
  onToggle: (which: ExpenseSummaryToggle) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const lastImport = useNavLastImportDate();

  const hasMoney = summary.totals.length > 0;
  // A jobb kártya CSAK akkor áll ki, ha van benne mondanivaló — üres doboz nem információ.
  const hasMeta =
    summary.uncategorized > 0 ||
    summary.salaryWithoutEmployee > 0 ||
    summary.unknownAmount > 0 ||
    summary.dismissed > 0 ||
    lastImport !== null;
  // Ha csak az EGYIK kártya van, az a teljes szélességet kapja (nem marad fél sor üresen).
  const alone = hasMoney !== hasMeta;

  return (
    <SummaryCards>
      {hasMoney && (
        <SummaryCard title={t('expenses.summary.title')} full={alone}>
          {/* KÉT HASÁB elválasztóval; a 2. oszlop kapja a hajszálvonalat, tehát három deviza esetén a
              második SORBAN is a jobb oldali kap választót — a rács rendje így marad olvasható. */}
          <div className="grid gap-4 sm:grid-cols-2 sm:gap-y-4">
            {summary.totals.map((row, i) => (
              <div
                key={row.currency}
                className={cn('min-w-0', i % 2 === 1 && 'sm:border-border sm:border-l sm:pl-5')}
              >
                <Text variant="secondary" as="div" className="text-xs">
                  {t('expenses.summary.netIn', { currency: row.currency })}
                </Text>
                <div className="text-foreground truncate text-lg font-semibold tabular-nums">
                  {formatMoney(row.netAmount, row.currency, locale)}
                </div>
                <Text tone="tertiary" as="div" className="truncate text-xs tabular-nums">
                  {t('expenses.summary.grossAndCount', {
                    gross: formatMoney(row.grossAmount, row.currency, locale),
                    count: row.count,
                  })}
                </Text>
              </div>
            ))}
          </div>
        </SummaryCard>
      )}

      {hasMeta && (
        <SummaryCard title={t('expenses.summary.statusTitle')} full={alone}>
          <div className="space-y-2">
            {/* TEENDŐK — a szám warning-tónusú, mert cselekvést kér. */}
            {summary.uncategorized > 0 && (
              <SummaryToggleRow
                label={t('expenses.summary.uncategorizedLabel')}
                value={summary.uncategorized}
                tone="warning"
                pressed={active.uncategorized}
                onClick={() => onToggle('uncategorized')}
              />
            )}
            {/* A MÁSODIK teendő: bér-típusú tételen ülő sor munkatárs nélkül. ÉLŐ állapot, nem import-nyom —
                a kézzel hiányosan felvett sort ugyanúgy megfogja, és addig marad, amíg valaki ki nem tölti. */}
            {summary.salaryWithoutEmployee > 0 && (
              <SummaryToggleRow
                label={t('expenses.summary.salaryWithoutEmployeeLabel')}
                value={summary.salaryWithoutEmployee}
                tone="warning"
                pressed={active.missingEmployee}
                onClick={() => onToggle('missingEmployee')}
              />
            )}
            {/* A végösszeg ALULBECSÜL, ha van összeg nélküli sor — ezt ki kell mondani, nem elhallgatni. */}
            {summary.unknownAmount > 0 && (
              <SummaryToggleRow
                label={t('expenses.summary.unknownAmountLabel')}
                value={summary.unknownAmount}
                tone="warning"
                pressed={active.missingAmount}
                onClick={() => onToggle('missingAmount')}
              />
            )}

            {/* PASSZÍV állapot és KAPCSOLÓ — elválasztó alatt, hogy ne keveredjen a teendőkkel. */}
            {(lastImport !== null || summary.dismissed > 0) &&
              (summary.uncategorized > 0 ||
                summary.salaryWithoutEmployee > 0 ||
                summary.unknownAmount > 0) && <div className="border-border/70 border-t pt-2" />}
            {lastImport !== null && (
              <SummaryMetaRow
                label={t('expenses.summary.navImportLabel')}
                // A DÁTUM a KÖZÖS formázón megy át (`formatDay`), mint a lista Dátumok oszlopa és minden más
                // dátum a rendszerben — nyelv szerint. A hook nyers ISO-napot ad; a formázás a HATÁRON
                // történik, itt.
                value={formatDay(lastImport, locale)}
                tone="muted"
              />
            )}
            {/* Az elvetettek nem tűnhetnek el NYOMTALANUL: a darabszám látszik, és egy kattintással elő is
                hívhatók. GOMB (nem jelvény), mert szűrőt állít — és aktív állapotban jelölt. */}
            {summary.dismissed > 0 && (
              <SummaryToggleRow
                label={t('expenses.summary.dismissedLabel')}
                value={summary.dismissed}
                pressed={active.dismissed}
                onClick={() => onToggle('dismissed')}
              />
            )}
          </div>
        </SummaryCard>
      )}
    </SummaryCards>
  );
}

/**
 * KATTINTHATÓ állapot-sor: GOMB (nem jelvény), mert szűrőt állít — aktív állapotban jelölt (`aria-pressed` +
 * háttér), a nyíl pedig jelzi, hogy a sor visz valahová. A tónus a sor FAJTÁJÁT mondja (teendő → warning),
 * a gomb-alak azt, hogy kattintható — a kettő független.
 */
function SummaryToggleRow({
  label,
  value,
  tone = 'default',
  pressed,
  onClick,
}: {
  label: string;
  value: number;
  tone?: 'default' | 'warning';
  pressed: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={cn(
        'focus-visible:ring-ring -mx-1.5 w-[calc(100%+0.75rem)] rounded-md px-1.5 py-0.5 text-left',
        'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2',
        pressed && 'bg-muted',
      )}
    >
      <SummaryMetaRow
        label={label}
        tone={tone}
        value={
          <span className="inline-flex items-center gap-0.5">
            {value}
            <ChevronRightIcon className="text-muted-foreground size-3.5" />
          </span>
        }
      />
    </button>
  );
}
